import React from 'react';
import cn from 'clsx';

import Button from '../../library/button/button';
import {
  AngleDoubleLeftRegularIcon,
  AngleDoubleRightRegularIcon,
  AngleLeftRegularIcon,
  AngleRightRegularIcon
} from '../../shared/icons/icons';

import styles from './pagination.scss';

interface PaginationProps {
  className?: string;
  currentPage?: number;
  numberOfPages?: number;
  numberOfPagesToShow?: number;
  onPageChange: (pageNumber: number) => void;
}

function Pagination(props: PaginationProps) {
  const {
    className,
    currentPage = 0,
    numberOfPages = 1,
    numberOfPagesToShow = 5,
    onPageChange
  } = props;

  function getPagesToDisplay(currentPage, numberOfPages, numberOfPagesToShow) {
    if (numberOfPagesToShow > numberOfPages) {
      return Array.from({ length: numberOfPages }, (_, pageNum) => pageNum);
    }

    let leftEdge = Math.max(
      0,
      Math.ceil(currentPage - numberOfPagesToShow / 2)
    );

    let rightEdge = Math.max(
      0,
      Math.ceil(currentPage + numberOfPagesToShow / 2)
    );

    //Shift the range right if it falls below zero
    if (leftEdge < 0) {
      rightEdge = rightEdge + Math.abs(leftEdge);
      leftEdge = 0;
    }

    //Shift the range left if it's above number of pages
    if (rightEdge > numberOfPages) {
      leftEdge = leftEdge - (rightEdge - numberOfPages);
      rightEdge = numberOfPages;
    }

    return Array.from({ length: numberOfPagesToShow }, (_, i) => leftEdge + i);
  }

  const pages = getPagesToDisplay(
    currentPage,
    numberOfPages,
    numberOfPagesToShow
  );

  // Don't show pagination if there is only one page of results
  if (numberOfPages === 1) return null;

  return (
    <div className={cn(styles['pagination'], `${className || ''} pagination`)}>
      {currentPage > 0 && (
        <>
          <Button
            ariaLabel="First Page"
            className={styles['icon-wrapper']}
            onClick={() => onPageChange(0)}
          >
            <AngleDoubleLeftRegularIcon
              className={cn(
                `${styles['page-number']} ${styles['icon']}`,
                'pagination__icon'
              )}
              data-testid="angle-double-left"
            />
          </Button>
          <Button
            ariaLabel="Previous Page"
            className={styles['icon-wrapper']}
            onClick={() => onPageChange(currentPage - 1)}
          >
            <AngleLeftRegularIcon
              className={cn(
                `${styles['page-number']} ${styles['icon']}`,
                'pagination__icon'
              )}
              data-testid="angle-left"
            />
          </Button>
        </>
      )}

      {pages.map(page => (
        <Button
          ariaLabel={`page ${page + 1}`}
          className={cn(
            page === currentPage
              ? `${styles['page-number']} ${styles['selected']}`
              : styles['page-number'],
            `pagination__page-number ${
              page === currentPage ? 'pagination__page-number--selected' : ''
            }`
          )}
          key={`pagination-count-${page}`}
          onClick={() => onPageChange(page)}
          size="small"
        >
          {page + 1}
        </Button>
      ))}
      {currentPage < numberOfPages - 1 && (
        <>
          <Button
            ariaLabel="Next Page"
            className={styles['icon-wrapper']}
            onClick={() => onPageChange(currentPage + 1)}
          >
            <AngleRightRegularIcon
              className={cn(
                `${styles['page-number']} ${styles['icon']}`,
                'pagination__icon'
              )}
              data-testid="angle-right"
            />
          </Button>
          <Button
            ariaLabel="Last Page"
            className={styles['icon-wrapper']}
            onClick={() => onPageChange(numberOfPages - 1)}
          >
            <AngleDoubleRightRegularIcon
              className={cn(
                `${styles['page-number']} ${styles['icon']}`,
                'pagination__icon'
              )}
              data-testid="angle-double-right"
            />
          </Button>
        </>
      )}
    </div>
  );
}

export default React.memo(Pagination);
